import { RestRequestGetWallets } from '@katanaperps/katana-perps-sdk';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getChainPrivateApi } from 'api/apiCoreSdk';
import { ApiWallet } from 'api/types';
import { useWebsocketEventListener } from 'components/hooks';
import { IGNORED_NONCE } from 'config/constants';
import { throttleInstance } from 'lib/throttle';
import { useSelector } from 'reduxState/store';

import { minutes, seconds } from './utils';

const throttleInvalidate = throttleInstance(2000);

export const useInvalidatePositionQueries = () => {
  const queryClient = useQueryClient();
  const walletAddress = useSelector(state => state.wallet.walletAddress);
  return () => {
    throttleInvalidate(() =>
      queryClient.invalidateQueries({
        queryKey: ['walletAndPositions', walletAddress],
      })
    );
  };
};

type PositionsState = ApiWallet | undefined;

export const useGlobalPositionQueriesInvalidatorAndSync = () => {
  // const walletAddress = useSelector(state => state.wallet.walletAddress);

  // const queryClient = useQueryClient();
  const invalidateQueries = useInvalidatePositionQueries();

  useWebsocketEventListener('orders', event => {
    if (!event.data.type) {
      // Liquidations/Deleverages are handled by "positions" event
      return;
    }

    if (event.data.status === 'open' || event.data.status === 'canceled') {
      return invalidateQueries();
    }

    if (event.data.fills && event.data.fills.length > 0) {
      invalidateQueries();
    }
  });

  useWebsocketEventListener('positions', _event => {
    // if (event.data.status === 'closed') {
    // Required Refetch all positions again - other positions might be affected
    invalidateQueries();
    //   return;
    // }
  });
  useWebsocketEventListener('deposits', _event => invalidateQueries());
  useWebsocketEventListener('withdrawals', _event => invalidateQueries());

  // TODO: this was preventing react-query interval runs
  // If tickers are subscribed, update positions with latest indexPrice
  // useWebsocketEventListener('tickers', event => {
  //   const tickerIndexPrice = event?.data.indexPrice;
  //   const tickerMarket = event?.data.market;
  //   if (!tickerMarket) {
  //     return;
  //   }
  //   const existingData = queryClient.getQueryData<PositionsState>([
  //     'walletAndPositions',
  //     walletAddress,
  //   ]);
  //   const matchedPosition = existingData?.positions?.find(
  //     position => position.market === tickerMarket
  //   );
  //   if (
  //     !tickerIndexPrice ||
  //     !matchedPosition ||
  //     matchedPosition.indexPrice === tickerIndexPrice
  //   ) {
  //     return;
  //   }
  //   const cache = queryClient.getQueryState([
  //     'walletAndPositions',
  //     walletAddress,
  //   ]);
  //   queryClient.setQueryData<PositionsState>(
  //     ['walletAndPositions', walletAddress],
  //     existingData =>
  //       existingData
  //         ? {
  //             ...existingData,
  //             positions: existingData.positions?.map(position =>
  //               tickerMarket === position.market
  //                 ? { ...position, indexPrice: tickerIndexPrice }
  //                 : position
  //             ),
  //           }
  //         : undefined,
  //     // Make sure we don't modify `updatedAt` as
  //     // it would mess Cache & Stale time
  //     { updatedAt: cache?.dataUpdatedAt }
  //   );
  // });
};

/**
 *  Note: it's already being refetched from WithdrawModalContent
 *  - maybe we can keep it that way, so each app section will refetch when needed
 */
export const useWalletWithPositionsQuery = (
  enabled = true,
  /** ~5s refetch interval. On some places eg. to get rank, we don't need such quick polling. */
  quickRefetch = true,
  /** Use vault instead of current wallet when provided */
  vaultManagerWallet?: string
) => {
  const currentWalletAddress = useSelector(state => state.wallet.walletAddress);
  const walletAddress = vaultManagerWallet || currentWalletAddress;
  return useQuery<PositionsState>({
    queryKey: ['walletAndPositions', walletAddress, vaultManagerWallet],
    queryFn:
      !walletAddress || !enabled
        ? skipToken
        : async () => {
            const params: RestRequestGetWallets = {
              wallet: walletAddress,
              nonce: IGNORED_NONCE,
            };
            const response = await (vaultManagerWallet
              ? getChainPrivateApi().getInternalWalletsForVault(params)
              : getChainPrivateApi().getWallets(params));
            const firstWallet = response[0];

            return {
              ...firstWallet,
              // Reverse ASC API order
              positions: firstWallet.positions?.reverse(),
            };
          },
    staleTime: quickRefetch ? seconds(5) : minutes(3),
    refetchOnWindowFocus: false,
    refetchInterval: quickRefetch ? seconds(6) : false,
  });
};

/**
 * Note that getting single position will fetch all positions.
 * This is done on purpose as user will be mostly traveling between list and detail.
 */
export const usePositionQuery = ({
  market,
  vaultManagerWallet,
}: {
  market: string | undefined;
  vaultManagerWallet?: string;
}) => {
  const result = useWalletWithPositionsQuery(
    !!market,
    undefined,
    vaultManagerWallet
  );
  return useMemo(() => {
    return {
      ...result,
      data: result.data?.positions?.find(
        position => position.market === market
      ),
    };
  }, [market, result]);
};
